<template>
  <v-app>
    <!-- Rotas Públicas -->
    <template v-if="$route.meta.public">
      <transition>
        <keep-alive>
          <router-view :key="$route.fullpath" />
        </keep-alive>
      </transition>
    </template>

    <!-- Rotas com Autenticação -->
    <template v-else>
      <!-- Barra, Menu Lateral -->
      <app-toolbar v-if="finished" />

      <!-- Rederização das Views --->
      <v-main class="background">
        <app-breadcrumbs v-show="finished" :items="breadcrumbItems" />
        <v-container fluid class="page-wrapper">
          <router-view v-if="finished" />
        </v-container>
      </v-main>

      <!-- Rodapé -->
      <v-footer height="auto" class="footer lighten-1 app--footer pa-3" app>
        <v-row no-gutters class="justify-center mt-0">
          <span class="caption">
            Desenvolvido por <b>Universidade Federal do Ceará (UFC)</b>
          </span>
        </v-row>
      </v-footer>
    </template>

    <!-- Componente de Carregamento -->
    <app-loading :visibilidade="!finished || loading" />

    <!-- Componente de Push Notification (Exibe Mensagens de Sucesso, Erro, etc...) -->
    <app-snackbar
      :descricao="snackbar.texto"
      :cor="snackbar.cor"
      :visibilidade="snackbar.visibilidade"
    />

    <!-- Componente de Avisos -->
    <dialog-info :value="aviso" />

    <!-- Componente dos Tutoriais -->
    <app-tutorial
      :tutorial="tutorial.nome"
      :dialog-visible="tutorial.visibilidade"
      :toggle-visible="toogleTutorial"
    />
  </v-app>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import AppToolbar from '@/components/core/AppToolbar';
import AppSnackbar from '@/components/core/AppSnackbar';
import AppLoading from '@/components/core/AppLoading';
import AppTutorial from '@/components/core/AppTutorial';
import AppBreadcrumbs from '@/components/core/AppBreadcrumbs';
import DialogInfo from '@/components/DialogInfo';

export default {
  name: 'App',

  components: {
    AppToolbar,
    AppSnackbar,
    AppLoading,
    AppTutorial,
    AppBreadcrumbs,
    DialogInfo,
  },

  data() {
    return {
      component: '',
      finished: false,
    };
  },

  computed: {
    ...mapState({
      loading: (state) => state.app.loading,
      snackbar: (state) => state.app.snackbar,
      tutorial: (state) => state.app.tutorial,
      breadcrumbItems: (state) => state.app.breadcrumbItems,
      aviso: (state) => state.app.aviso,
      loginExpirado: (state) => state.app.loginExpirado,
      usuarioNaoCadastrado: (state) => state.app.usuarioNaoCadastrado,
    }),
  },

  watch: {
    loginExpirado() {
      this.dispatchInfoLogin();
    },
    usuarioNaoCadastrado() {
      this.dispatchInfoLogin();
    },
  },

  updated() {
    if (this.component !== this.$router.history.current.name) {
      this.component = this.$router.history.current.name;
      if (window.scrollY > 0) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  },

  async created() {
    // Recupera config do modo escuro do sistema do navegador
    const systemDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const askDark = window.localStorage.getItem('askDark') == null;

    // Troca tema escuro
    this.$vuetify.theme.dark = window.localStorage.getItem('dark') === 'true';

    // Check o tema escuro do sistema
    if (askDark && systemDarkTheme && !this.$vuetify.theme.dark) {
      this.$store.dispatch('app/setAviso', {
        texto: 'Detectamos que o seu sistema está com tema escuro ativado. Deseja ativar o modo escuro no desenvolva '
          + 'também?',
        observasoes: 'O modo escuro usa cores com menos brilho, isso diminui o cansaço visual, melhora a '
          + 'legibilidade das páginas e ainda pode ajudar a bateria do celular ou notebook durar mais. À noite ou '
          + 'em ambientes escuros, o modo escuro é a opção mais confortável para sua visão em frente à '
          + 'tela, já que a alta luminosidade é muito prejudicial aos olhos.',
        visibilidade: true,
        acoes: {
          confirm: {
            texto: 'SIM',
            cor: 'secondary',
            funcao: () => {
              this.$vuetify.theme.dark = true;
              window.localStorage.setItem('dark', this.$vuetify.theme.dark);
              this.toogleDialogInfo();
            },
          },
          cancel: {
            texto: 'NÃO',
            cor: 'error',
            funcao: () => {
              this.$vuetify.theme.dark = false;
              window.localStorage.setItem('askDark', false);
              this.toogleDialogInfo();
            },
          },
        },
      });
    }

    // Alerta se o login expirou ou se o usuário ainda não está cadastrado no sistema
    this.dispatchInfoLogin();

    // Carga de dados e montagem dos componentes
    if (this.$keycloak.authenticated) {
      await this.$store.dispatch('auth/load');
      this.component = this.$router.history.current.name;
      this.finished = true;
    }
  },

  methods: {
    ...mapActions('app', ['setBreadcrumbs']),
    dispatchInfoLogin() {
      let texto;
      let funcao;

      if (this.loginExpirado || this.usuarioNaoCadastrado) {
        if (this.loginExpirado) {
          texto = 'Você não está conectado. Por favor, faça login novamente no sistema.';
          funcao = () => {
            this.$keycloak.login({ scope: 'id_institucional' });
          };
        }

        if (this.usuarioNaoCadastrado) {
          texto = 'Desculpe. Usuário ainda não registrado.';
          funcao = () => {
            this.$keycloak.logout();
          };
        }

        this.$store.dispatch('app/setAviso', {
          texto,
          visibilidade: true,
          acoes: {
            confirm: {
              texto: 'OK',
              cor: 'secondary',
              funcao,
            },
          },
        });
      }
    },
    toogleTutorial() {
      this.tutorial.visibilidade = !this.tutorial.visibilidade;
    },
    toogleDialogInfo() {
      this.aviso.visibilidade = !this.aviso.visibilidade;
    },
  },
};
</script>

<style lang="stylus">
.page-wrapper {
  min-height: calc(100vh - 64px - 50px - 81px);
}

.row {
  margin-top: 15px;
}

.v-card__text {
  font-size: 1rem !important;
}

.col-4 {
  position: relative;
  margin-top: -4em;
}

.v-breadcrumbs {
  width: 60%;
}

.v-breadcrumbs__item {
  position: relative;
  top: 8px;
}

.v-breadcrumbs__divider {
  position: relative;
  top: 8px;
}
</style>
